import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { ITransformRT2, IVector2 } from '@seddi/math-v2';
import type { LogoTechnique } from '@seddi/scene';
import type { SceneDesc } from '@seddi/ui-three-viewer';
import type { StyleLogoRegion } from '../api/styleAggregator';
import { type MockupLogo, normalizeMockupLogo, normalizeMockupLogos } from '../types/mockupLogos';

// full scene payload out of Redux

let sceneStateInternal: SceneDesc | null = null;

export const getScene = () => {
  return sceneStateInternal;
};

export const setScene = (scene: SceneDesc | null) => {
  sceneStateInternal = scene;
};

const ResolutionDef: IVector2 = { x: 1024, y: 1024 };

// scene state

export type TAppState = {
  resolution: IVector2;
  sceneIsLoading: boolean;
  /** URL of the style preview image shown while the 3D scene is loading. */
  stylePreviewUrl: string | null;
  /** Shown in viewer when no styles found (e.g. StyleNotFoundError). */
  styleNotFoundMessage: string | null;
  /** Current style type (e.g. "headwear") for camera framing. */
  styleType: string | null;
  // simplified scene state propagation
  sceneVersion: number;
  /** Logos from mockup (mockup_id mode) to display in the viewer. */
  mockupLogos: MockupLogo[];
  mockupHideTearAwayLabel: boolean;
  /** Selected graphic ID */
  selectedGraphicId: string | null;
  /** Hovered graphic ID */
  hoverGraphicId: string | null;
  /** Style regions */
  styleRegions: StyleLogoRegion[];
};

const initialState: TAppState = {
  resolution: { ...ResolutionDef },
  sceneIsLoading: false,
  stylePreviewUrl: null,
  styleNotFoundMessage: null,
  styleType: null,
  sceneVersion: 0,
  mockupLogos: [],
  mockupHideTearAwayLabel: false,
  selectedGraphicId: null,
  hoverGraphicId: null,
  styleRegions: [],
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    clear: state => {
      state.resolution = { ...ResolutionDef };
      state.sceneIsLoading = false;
      state.stylePreviewUrl = null;
      state.styleNotFoundMessage = null;
      state.styleType = null;
      state.sceneVersion = 0;
      state.mockupLogos = [];
      state.mockupHideTearAwayLabel = false;
      state.selectedGraphicId = null;
      state.hoverGraphicId = null;
      state.styleRegions = [];
    },
    setResolution: (state, action: PayloadAction<IVector2>) => {
      state.resolution = action.payload;
    },
    setSceneIsLoading: (state, action: PayloadAction<boolean>) => {
      state.sceneIsLoading = action.payload;
    },
    setStylePreviewUrl: (state, action: PayloadAction<string | null>) => {
      state.stylePreviewUrl = action.payload;
    },
    setStyleNotFoundMessage: (state, action: PayloadAction<string | null>) => {
      state.styleNotFoundMessage = action.payload;
    },
    setStyleType: (state, action: PayloadAction<string | null>) => {
      state.styleType = action.payload;
    },
    updateScene: state => {
      state.sceneVersion = state.sceneVersion + 1;
    },
    setMockupLogos: (
      state,
      action: PayloadAction<{ logos: MockupLogo[]; hideTearAwayLabel: boolean }>,
    ) => {
      state.mockupLogos = normalizeMockupLogos(action.payload.logos);
      state.mockupHideTearAwayLabel = action.payload.hideTearAwayLabel;
    },
    clearMockupLogos: state => {
      state.mockupLogos = [];
      state.mockupHideTearAwayLabel = false;
      state.selectedGraphicId = null;
    },
    setSelectedGraphicId: (state, action: PayloadAction<string | null>) => {
      state.selectedGraphicId = action.payload;
    },
    setHoverGraphicId: (state, action: PayloadAction<string | null>) => {
      state.hoverGraphicId = action.payload;
    },
    updateGraphicTransform: (
      state,
      action: PayloadAction<{ id: string; translation: { x: number; y: number } }>,
    ) => {
      const logo = state.mockupLogos.find(l => l.id === action.payload.id);
      if (logo) {
        logo.transform.translation = action.payload.translation;
      }
    },
    setGraphicTransform: (
      state,
      action: PayloadAction<{ id: string; transform: ITransformRT2 }>,
    ) => {
      const logo = state.mockupLogos.find(l => l.id === action.payload.id);
      if (logo) {
        logo.transform = action.payload.transform;
      }
    },
    updateGraphicDisplayName: (
      state,
      action: PayloadAction<{ id: string; displayName: string }>,
    ) => {
      const logo = state.mockupLogos.find(l => l.id === action.payload.id);
      if (logo) {
        logo.displayName = action.payload.displayName;
      }
    },
    updateGraphicRegion: (state, action: PayloadAction<{ id: string; region: string }>) => {
      const logo = state.mockupLogos.find(l => l.id === action.payload.id);
      if (logo) {
        logo.region = action.payload.region;
      }
    },
    updateGraphicSize: (state, action: PayloadAction<{ id: string; size: IVector2 }>) => {
      const logo = state.mockupLogos.find(l => l.id === action.payload.id);
      if (logo) {
        logo.size = action.payload.size;
      }
    },
    updateGraphicTechnique: (
      state,
      action: PayloadAction<{ id: string; technique: LogoTechnique }>,
    ) => {
      const logo = state.mockupLogos.find(l => l.id === action.payload.id);
      if (logo) {
        logo.technique = action.payload.technique;
      }
    },
    updateGraphicCustomTechnique: (
      state,
      action: PayloadAction<{ id: string; customTechnique: string }>,
    ) => {
      const logo = state.mockupLogos.find(l => l.id === action.payload.id);
      if (logo) {
        logo.customTechnique = action.payload.customTechnique;
      }
    },
    removeGraphic: (state, action: PayloadAction<string>) => {
      state.mockupLogos = state.mockupLogos.filter(l => l.id !== action.payload);
      if (state.selectedGraphicId === action.payload) {
        state.selectedGraphicId = null;
      }
    },
    addGraphic: (state, action: PayloadAction<MockupLogo>) => {
      state.mockupLogos = [...state.mockupLogos, normalizeMockupLogo(action.payload)];
      state.selectedGraphicId = action.payload.id;
    },
    replaceGraphicImage: (
      state,
      action: PayloadAction<{
        id: string;
        imageSrc: string;
        imageSize: IVector2;
        size: IVector2;
      }>,
    ) => {
      const logo = state.mockupLogos.find(l => l.id === action.payload.id);
      if (logo) {
        logo.imageSrc = action.payload.imageSrc;
        logo.imageSize = action.payload.imageSize;
        logo.size = action.payload.size;
        logo.recoloredImageSrc = undefined;
        logo.recoloredId = undefined;
      }
    },
    setStyleRegions: (state, action: PayloadAction<StyleLogoRegion[]>) => {
      state.styleRegions = action.payload;
    },
  },
});

export const {
  clear,
  setResolution,
  setSceneIsLoading,
  setStylePreviewUrl,
  setStyleNotFoundMessage,
  setStyleType,
  updateScene,
  setMockupLogos,
  clearMockupLogos,
  setSelectedGraphicId,
  setHoverGraphicId,
  updateGraphicTransform,
  setGraphicTransform,
  updateGraphicDisplayName,
  updateGraphicRegion,
  updateGraphicSize,
  updateGraphicTechnique,
  updateGraphicCustomTechnique,
  removeGraphic,
  addGraphic,
  replaceGraphicImage,
  setStyleRegions,
} = appSlice.actions;

export const appReducer = appSlice.reducer;
